'use client';

import { useEffect, useMemo, useState } from 'react';

import { Button, Select, Skeleton, Typography } from 'lighthouse';

import CatalogProductCard from '@/widgets/CatalogProductCard';
import { catalogItemFromProduct } from '@/widgets/CatalogProductCard/mappers';
import { PriceBasisNote } from '@/widgets/PriceBasisNote';
import SupportTrigger from '@/widgets/SupportTrigger';

import { useCityStore } from '@/stores/city/useCityStore';

import { BATTERY_LANDING_PRODUCTS_PAGE_SIZE } from '@/constants/batteryLanding';
import { BATTERY_PRODUCT_HIDDEN_DISCOUNT_PERCENTAGE, batteryHeights, batteryPoles } from '@/constants/batteryProduct';
import { PRODUCTS_SORT_AVAILABLE_DESC_AND_SELLING_PRICE_ASC_ID } from '@/constants/common';
import { companyLandings } from '@/constants/companyLanding';

import { CompanyCatalogProps } from './models';

import { getProductsBatteryByVehicleFiltersService } from '@/client/services/core/productBattery/methods';
import { ProductBatteryType } from '@/server/services/core/productBattery/models';

export const CompanyCatalog = ({ slug, initialProducts, initialCityId, initialError }: CompanyCatalogProps) => {
    const company = companyLandings[slug];
    const cityId = useCityStore((state) => state.cityId);
    const [result, setResult] = useState({ products: initialProducts, cityId: initialCityId, error: initialError });
    const [isLoading, setIsLoading] = useState(false);
    const [retry, setRetry] = useState(0);
    const [category, setCategory] = useState('all');
    const [brand, setBrand] = useState('all');
    const [ampere, setAmpere] = useState('all');
    const [sort, setSort] = useState('price-asc');
    const [visibleCount, setVisibleCount] = useState(12);

    useEffect(() => {
        if (!company.brands.length) return;
        if (cityId === initialCityId && retry === 0) {
            setResult({ products: initialProducts, cityId: initialCityId, error: initialError });
            setIsLoading(false);
            return;
        }
        let ignored = false;
        setIsLoading(true);
        const load = async () => {
            const products: ProductBatteryType[] = [];
            let page = 0;
            let last = false;
            while (!last) {
                const response = await getProductsBatteryByVehicleFiltersService({
                    brandIds: company.brands.map((item) => item.id),
                    categoryIds: [1, 2],
                    cityId,
                    page,
                    size: BATTERY_LANDING_PRODUCTS_PAGE_SIZE,
                    sortOption: PRODUCTS_SORT_AVAILABLE_DESC_AND_SELLING_PRICE_ASC_ID
                });
                if (ignored) return;
                products.push(...response.data.content);
                last = response.data.last || !response.data.content.length;
                page += 1;
            }
            if (!ignored) setResult({ products, cityId, error: false });
        };
        load()
            .catch(() => {
                if (!ignored) setResult({ products: [], cityId, error: true });
            })
            .finally(() => {
                if (!ignored) setIsLoading(false);
            });
        return () => {
            ignored = true;
        };
    }, [cityId, initialCityId, initialProducts, initialError, company, retry]);

    const products = useMemo(
        () => result.products.filter((product) => product.discountPercentage !== BATTERY_PRODUCT_HIDDEN_DISCOUNT_PERCENTAGE),
        [result.products]
    );
    const categoryProducts = products.filter((product) => category === 'all' || product.category.id === Number(category));
    const amperes = [...new Set(categoryProducts.map((product) => product.ampere))].sort((a, b) => a - b);
    const filtered = categoryProducts
        .filter((product) => (brand === 'all' || product.brand.id === Number(brand)) && (ampere === 'all' || product.ampere === Number(ampere)))
        .sort(
            (a, b) =>
                Number(b.available) - Number(a.available) ||
                (sort === 'price-desc'
                    ? b.sellingPrice - a.sellingPrice
                    : sort === 'ampere'
                      ? a.ampere - b.ampere || a.sellingPrice - b.sellingPrice
                      : a.sellingPrice - b.sellingPrice)
        );
    const busy = isLoading || (company.brands.length > 0 && result.cityId !== cityId);
    const reset = () => {
        setCategory('all');
        setBrand('all');
        setAmpere('all');
        setSort('price-asc');
        setVisibleCount(12);
    };

    return (
        <section id='company-products' className='desktop-width scroll-mt-24 px-4 py-12 md:py-16' aria-labelledby='company-catalog-title'>
            <div className='mb-6 flex flex-wrap items-end justify-between gap-3'>
                <div>
                    <Typography variant='caption' component='p' color='primary' className='mb-2 font-bold'>
                        مقایسه کن، بعد انتخاب کن
                    </Typography>
                    <Typography id='company-catalog-title' variant='h5' component='h2' className='font-black'>
                        محصولات {company.name}
                    </Typography>
                </div>
                <Typography variant='caption' component='p' color='blackMediumEmphasis'>
                    قیمت و موجودی بر اساس شهر انتخاب‌شده در بالای سایت
                </Typography>
            </div>
            {!company.brands.length ? (
                <div className='rounded-2xl border border-N200 bg-white p-6 md:p-10'>
                    <Typography variant='subtitle1' component='h3' className='font-bold'>
                        فعلاً محصولی از {company.name} برای نمایش نداریم
                    </Typography>
                    <Typography variant='body2' component='p' color='blackMediumEmphasis' className='mt-3 leading-8'>
                        برای بررسی مدل موردنظرت از پشتیبانی کمک بگیر یا باتری‌های سایر برندها را ببین.
                    </Typography>
                    <div className='mt-5 flex flex-wrap items-center gap-4'>
                        <Button component='a' href='/car-battery/' variant='contained' className='text-white'>
                            همه باتری‌های ماشین
                        </Button>
                        <SupportTrigger source='landing_help' categoryId='battery' className='text-sm font-bold text-primary'>
                            راهنمایی برای خرید
                        </SupportTrigger>
                    </div>
                </div>
            ) : (
                <>
                    <div className='mb-5 rounded-2xl border border-N200 bg-white p-4 md:p-5'>
                        <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
                            {[
                                {
                                    id: 'category',
                                    label: 'نوع وسیله',
                                    value: category,
                                    onChange: (value: string) => {
                                        setCategory(value);
                                        setAmpere('all');
                                    },
                                    options: [
                                        ['all', 'همه وسایل'],
                                        ['1', 'ماشین'],
                                        ['2', 'موتورسیکلت']
                                    ]
                                },
                                {
                                    id: 'brand',
                                    label: 'برند',
                                    value: brand,
                                    onChange: setBrand,
                                    options: [['all', 'همه برندها'], ...company.brands.map((item) => [String(item.id), item.name])]
                                },
                                {
                                    id: 'ampere',
                                    label: 'ظرفیت باتری',
                                    value: ampere,
                                    onChange: setAmpere,
                                    options: [
                                        ['all', 'همه آمپرها'],
                                        ...amperes.map((value) => [String(value), `${value.toLocaleString('fa-IR')} آمپر`])
                                    ]
                                },
                                {
                                    id: 'sort',
                                    label: 'مرتب‌سازی',
                                    value: sort,
                                    onChange: setSort,
                                    options: [
                                        ['price-asc', 'ارزان‌ترین'],
                                        ['price-desc', 'گران‌ترین'],
                                        ['ampere', 'آمپر کمتر']
                                    ]
                                }
                            ].map((filter) => (
                                <Select
                                    key={filter.id}
                                    id={`company-filter-${filter.id}`}
                                    label={filter.label}
                                    value={filter.value}
                                    fullWidth
                                    size='small'
                                    menuItems={filter.options.map(([value, label]) => ({ value, label }))}
                                    SelectProps={{ MenuProps: { container: () => document.getElementById('app') } }}
                                    onChange={(event) => {
                                        filter.onChange(String(event.target.value));
                                        setVisibleCount(12);
                                    }}
                                />
                            ))}
                        </div>
                        <div className='mt-3 flex justify-end'>
                            <Button variant='text' size='small' onClick={reset}>
                                پاک کردن فیلترها
                            </Button>
                        </div>
                    </div>
                    <PriceBasisNote className='mb-6' />
                    <div aria-live='polite' aria-atomic='true' className='mb-4 text-sm text-blackMediumEmphasis'>
                        {busy
                            ? 'در حال دریافت قیمت و موجودی…'
                            : result.error
                              ? 'دریافت محصولات انجام نشد'
                              : `${filtered.length.toLocaleString('fa-IR')} محصول`}
                    </div>
                    {busy ? (
                        <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' aria-busy='true'>
                            {[0, 1, 2, 3].map((item) => (
                                <Skeleton key={item} variant='rounded' height={320} />
                            ))}
                        </div>
                    ) : result.error ? (
                        <div role='alert' className='rounded-2xl border border-N200 bg-white p-8 text-center'>
                            <Typography variant='body2' component='p' className='mb-4 leading-7'>
                                دریافت قیمت و موجودی با مشکل مواجه شد. دوباره تلاش کن؛ محتوای صفحه همچنان در دسترس است.
                            </Typography>
                            <Button variant='contained' onClick={() => setRetry((value) => value + 1)} className='text-white'>
                                تلاش دوباره
                            </Button>
                        </div>
                    ) : filtered.length ? (
                        <>
                            <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
                                {filtered.slice(0, visibleCount).map((product) => (
                                    <CatalogProductCard
                                        key={product.uuid}
                                        isCompact
                                        item={catalogItemFromProduct(product)}
                                        specs={[
                                            `${product.ampere.toLocaleString('fa-IR')} آمپر`,
                                            batteryHeights[product.height],
                                            product.category.id === 1 ? batteryPoles[product.pole] : '',
                                            product.guarantee ? `گارانتی ${product.guarantee.toLocaleString('fa-IR')} ماه` : ''
                                        ].filter(Boolean)}
                                        priceNote={`با تحویل باتری قدیمی ${product.ampere.toLocaleString('fa-IR')} آمپر`}
                                    />
                                ))}
                            </div>
                            {visibleCount < filtered.length && (
                                <div className='mt-8 text-center'>
                                    <Button variant='outlined' onClick={() => setVisibleCount((value) => value + 12)}>
                                        نمایش محصولات بیشتر
                                    </Button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className='rounded-2xl border border-dashed border-N200 bg-white p-8 text-center'>
                            <Typography variant='body2' component='p' className='leading-8'>
                                برای این انتخاب محصولی پیدا نشد. فیلترها را تغییر بده یا برای بررسی گزینه‌های سازگار از ما کمک بگیر.
                            </Typography>
                            <div className='mt-4 flex flex-wrap items-center justify-center gap-4'>
                                <Button variant='outlined' onClick={reset}>
                                    بازنشانی فیلترها
                                </Button>
                                <SupportTrigger source='landing_help' categoryId='battery' className='text-sm font-bold text-primary'>
                                    پشتیبانی و راهنمایی
                                </SupportTrigger>
                            </div>
                        </div>
                    )}
                </>
            )}
        </section>
    );
};
